// Luxurious alarm and timer sounds using Web Audio API
// No external files needed - all sounds generated programmatically

type SoundType = 'gentle-chime' | 'bell' | 'modern-pulse' | 'timer-complete' | 'tick';

class ClockSoundGenerator {
  private audioContext: AudioContext | null = null;

  private getAudioContext(): AudioContext {
    if (!this.audioContext) {
      this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    }
    return this.audioContext;
  }

  // Gentle chime - soft, luxurious bell tone
  private playGentleChime(duration: number = 2000) {
    const ctx = this.getAudioContext();
    const now = ctx.currentTime;

    // Create multiple oscillators for rich harmonic content
    const frequencies = [523.25, 659.25, 783.99]; // C5, E5, G5 (major chord)
    
    frequencies.forEach((freq, index) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      
      osc.type = 'sine';
      osc.frequency.setValueAtTime(freq, now);
      
      // Fade in and out envelope
      gain.gain.setValueAtTime(0, now);
      gain.gain.linearRampToValueAtTime(0.15 / (index + 1), now + 0.01);
      gain.gain.exponentialRampToValueAtTime(0.001, now + duration / 1000);
      
      osc.connect(gain);
      gain.connect(ctx.destination);
      
      osc.start(now);
      osc.stop(now + duration / 1000);
    });
  }

  // Bell - classic alarm bell sound
  private playBell(duration: number = 1500) {
    const ctx = this.getAudioContext();
    const now = ctx.currentTime;

    // Bell frequencies with overtones
    const frequencies = [800, 1200, 1600, 2000];
    
    for (let i = 0; i < 3; i++) {
      setTimeout(() => {
        frequencies.forEach((freq, index) => {
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          
          osc.type = 'sine';
          osc.frequency.setValueAtTime(freq, ctx.currentTime);
          
          gain.gain.setValueAtTime(0, ctx.currentTime);
          gain.gain.linearRampToValueAtTime(0.2 / (index + 1), ctx.currentTime + 0.001);
          gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.3);
          
          osc.connect(gain);
          gain.connect(ctx.destination);
          
          osc.start(ctx.currentTime);
          osc.stop(ctx.currentTime + 0.3);
        });
      }, i * 400);
    }
  }

  // Modern pulse - sophisticated digital alarm
  private playModernPulse(duration: number = 2000) {
    const ctx = this.getAudioContext();
    const now = ctx.currentTime;

    const pulseCount = 4;
    const pulseInterval = 0.25;

    for (let i = 0; i < pulseCount; i++) {
      const startTime = now + i * pulseInterval;
      
      // High frequency pulse
      const osc1 = ctx.createOscillator();
      const gain1 = ctx.createGain();
      
      osc1.type = 'sine';
      osc1.frequency.setValueAtTime(1046.50, startTime); // C6
      
      gain1.gain.setValueAtTime(0, startTime);
      gain1.gain.linearRampToValueAtTime(0.3, startTime + 0.01);
      gain1.gain.exponentialRampToValueAtTime(0.001, startTime + 0.15);
      
      osc1.connect(gain1);
      gain1.connect(ctx.destination);
      
      osc1.start(startTime);
      osc1.stop(startTime + 0.15);
      
      // Lower harmony
      const osc2 = ctx.createOscillator();
      const gain2 = ctx.createGain();
      
      osc2.type = 'sine';
      osc2.frequency.setValueAtTime(783.99, startTime + 0.05); // G5
      
      gain2.gain.setValueAtTime(0, startTime + 0.05);
      gain2.gain.linearRampToValueAtTime(0.2, startTime + 0.06);
      gain2.gain.exponentialRampToValueAtTime(0.001, startTime + 0.2);
      
      osc2.connect(gain2);
      gain2.connect(ctx.destination);
      
      osc2.start(startTime + 0.05);
      osc2.stop(startTime + 0.2);
    }
  }

  // Timer complete - triumphant completion sound
  private playTimerComplete(duration: number = 1500) {
    const ctx = this.getAudioContext();
    const now = ctx.currentTime;

    // Ascending arpeggio: C5, E5, G5, C6
    const notes = [
      { freq: 523.25, time: 0 },
      { freq: 659.25, time: 0.1 },
      { freq: 783.99, time: 0.2 },
      { freq: 1046.50, time: 0.3 },
    ];

    notes.forEach(({ freq, time }) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      
      osc.type = 'sine';
      osc.frequency.setValueAtTime(freq, now + time);
      
      gain.gain.setValueAtTime(0, now + time);
      gain.gain.linearRampToValueAtTime(0.25, now + time + 0.01);
      gain.gain.exponentialRampToValueAtTime(0.001, now + time + 0.4);
      
      osc.connect(gain);
      gain.connect(ctx.destination);
      
      osc.start(now + time);
      osc.stop(now + time + 0.4);
    });
  }

  // Subtle tick sound for countdown
  private playTick() {
    const ctx = this.getAudioContext();
    const now = ctx.currentTime;

    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    
    osc.type = 'sine';
    osc.frequency.setValueAtTime(800, now);
    
    gain.gain.setValueAtTime(0, now);
    gain.gain.linearRampToValueAtTime(0.05, now + 0.001);
    gain.gain.exponentialRampToValueAtTime(0.001, now + 0.03);
    
    osc.connect(gain);
    gain.connect(ctx.destination);
    
    osc.start(now);
    osc.stop(now + 0.03);
  }

  // Public API
  public play(type: SoundType, duration?: number): void {
    try {
      switch (type) {
        case 'gentle-chime':
          this.playGentleChime(duration);
          break;
        case 'bell':
          this.playBell(duration);
          break;
        case 'modern-pulse':
          this.playModernPulse(duration);
          break;
        case 'timer-complete':
          this.playTimerComplete(duration);
          break;
        case 'tick':
          this.playTick();
          break;
        default:
          this.playGentleChime(duration);
      }
    } catch (error) {
      console.error('[ClockSound] Failed to play sound:', error);
    }
  }

  // Play repeating alarm until stopped
  public playAlarm(type: SoundType = 'gentle-chime', intervalMs: number = 3000): () => void {
    let intervalId: NodeJS.Timeout | null = null;
    
    // Play immediately
    this.play(type);
    
    // Then repeat
    intervalId = setInterval(() => {
      this.play(type);
    }, intervalMs);

    // Return stop function
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }
}

// Singleton instance
export const clockSounds = new ClockSoundGenerator();

// Utility to request audio permission (some browsers require user interaction)
export const requestAudioPermission = async (): Promise<boolean> => {
  try {
    const ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
    await ctx.resume();
    return true;
  } catch (error) {
    console.error('[ClockSound] Audio permission denied:', error);
    return false;
  }
};
